/**
 * home_widgets.ts — 홈(대시보드) 위젯 카탈로그 + 노출 판정 (순수 로직, Firebase 의존 없음).
 *
 *   Dashboard.tsx 안에 있던 AVAILABLE_WIDGETS·SOURCE_PERMS·canShowWidget 을 꺼낸 것(2026-11-02) —
 *   그룹(부서) 편집기가 "이 그룹의 홈에 켤 위젯"을 고르려면 같은 목록이 필요해서다.
 *
 * 노출 판정 두 겹:
 *   ① 데이터 출처 권한(2026-10-12) — 위젯이 읽는 출처마다 읽기 권한(anyOf), 위젯은 쓰는 출처를 모두(AND) 만족.
 *      출처 권한 키는 firestore.rules 의 read 게이트와 같다. 새 위젯은 sources 를 반드시 적는다.
 *   ② 그룹 위젯 설정(2026-11-02) — 그룹 문서 `home_widgets[widgetKey] === false` 면 숨김. 값이 없으면 켜짐(기본).
 *      관리자(is_admin)는 ②를 건너뛴다. 권한이 있어도 그 부서 홈에 필요 없는 위젯을 치우는 용도.
 */
import type { IconName } from "@/components/ui/Icon";

export type HomeWidgetType = "kpi" | "bar" | "bar-combo" | "line" | "table" | "month-table" | "today2" | "calendar";

export type WidgetSource = "shipStats" | "shipLive" | "sales" | "rawPrices";

export const SOURCE_PERMS: Record<WidgetSource, readonly string[]> = {
  /** 일·월별 출하 집계 (daily_shipments · shipment_monthly) */
  shipStats: ["shipment.view", "dispatch.view", "stats.view"],
  /** 금일 출하 명세 — 현장·거래처 단위 (출하조회 화면과 같은 소스) */
  shipLive: ["shipment.view", "dispatch.view"],
  /** 매출·수금·미수·관급 배정 (sales_*) */
  sales: ["sales.view"],
  /**
   * 원재료 단가 (raw_materials) — 원자재 단가 권한자만(material.price, 옛 material.view).
   * ledger.view(수량만 보는 입출고)·cost.view(원가 화면 전용)는 뺐다(부서별-권한 §3·§5-B).
   * 화면 가림만 — raw_materials 읽기 차단은 단가 분리(§5-C) 때 rules 로.
   */
  rawPrices: ["material.price", "material.view"],
};

export interface HomeWidgetDef {
  type: HomeWidgetType;
  kpiIndex?: number;
  label: string;
  icon: IconName;
  defaultSize: { w: number; h: number };
  sources: WidgetSource[];
  /** true 면 여러 인스턴스 추가 가능(인스턴스마다 고유 id). 미지정/false 면 id=type 1개만 */
  multi?: boolean;
  /**
   * true 면 경원 아스콘 공간에서도 보인다 — 출하 위젯만 OKIS 미러로 바뀐다(hooks/useHomeShipments, 2026-10-22).
   * 나머지(매출·단가·수금·미수·관급 잔량 = 중앙 영업 미러, 가동 차량 = 중앙 차량 등록부, 원재료비 = 중앙 자재)는
   * 경원 자료가 아직 없어 경원 홈에서 숨긴다(저장된 배치는 그대로 — 중앙 홈에서는 원래 자리).
   */
  ascon?: boolean;
}

export const HOME_WIDGETS: readonly HomeWidgetDef[] = [
  { type: "today2",           label: "금일 출하 현황", icon: "truck", defaultSize: { w: 12, h: 2 }, sources: ["shipLive"], ascon: true },
  { type: "kpi", kpiIndex: 0, label: "당월 출하량 KPI", icon: "truck",     defaultSize: { w: 3, h: 2 }, sources: ["shipStats"], ascon: true },
  { type: "kpi", kpiIndex: 1, label: "당월 매출 KPI",   icon: "coin",      defaultSize: { w: 3, h: 2 }, sources: ["sales"] },
  { type: "kpi", kpiIndex: 2, label: "평균 단가 KPI",   icon: "cost",      defaultSize: { w: 3, h: 2 }, sources: ["sales"] },
  { type: "kpi", kpiIndex: 3, label: "가동 차량 KPI",   icon: "truck",     defaultSize: { w: 3, h: 2 }, sources: [] },
  { type: "kpi", kpiIndex: 4, label: "당월 수금 KPI",   icon: "coin",      defaultSize: { w: 3, h: 2 }, sources: ["sales"] },
  { type: "kpi", kpiIndex: 5, label: "미수 잔액 KPI",   icon: "cost",      defaultSize: { w: 3, h: 2 }, sources: ["sales"] },
  { type: "kpi", kpiIndex: 6, label: "관급 잔량 KPI",   icon: "ledger",    defaultSize: { w: 3, h: 2 }, sources: ["sales"] },
  { type: "kpi", kpiIndex: 7, label: "금일 진척률 KPI", icon: "truck",     defaultSize: { w: 3, h: 2 }, sources: ["shipLive"], ascon: true },
  { type: "bar",              label: "월별 출하량 차트", icon: "dashboard", defaultSize: { w: 6, h: 4 }, sources: ["shipStats"], ascon: true },
  { type: "bar-combo",        label: "월별 출하량 + 합계선", icon: "dashboard", defaultSize: { w: 12, h: 4 }, sources: ["shipStats"], ascon: true },
  { type: "line",             label: "원재료비 추이",   icon: "market",    defaultSize: { w: 6, h: 4 }, sources: ["rawPrices"] },
  { type: "month-table",      label: "월별 출하량 표",  icon: "ledger",    defaultSize: { w: 6, h: 5 }, sources: ["shipStats"], ascon: true },
  { type: "table",            label: "금일 출하 내용",  icon: "ledger",    defaultSize: { w: 12, h: 5 }, sources: ["shipLive"], ascon: true },
  // 캘린더는 출하·생일 표시를 위젯 안에서 권한별로 끈다 — 위젯 자체는 누구나
  { type: "calendar",         label: "캘린더",          icon: "calendar",  defaultSize: { w: 4, h: 4 }, sources: [], multi: true, ascon: true },
];

/** kpiIndex → 위젯 id suffix (단일 소스. HOME_WIDGETS/onAdd/AddWidgetMenu 공유) */
export const KPI_ID_SUFFIX = ["ship", "rev", "price", "fleet", "receipt", "ar", "alloc", "today"] as const;
export function kpiWidgetId(kpiIndex: number): string {
  return `kpi-${KPI_ID_SUFFIX[kpiIndex]}`;
}

export type WidgetRef = Pick<HomeWidgetDef, "type" | "kpiIndex">;

export function widgetDef(w: WidgetRef): HomeWidgetDef | undefined {
  return HOME_WIDGETS.find((a) => a.type === w.type && (a.kpiIndex ?? null) === (w.kpiIndex ?? null));
}

/**
 * 그룹 위젯 설정·기본 배치가 쓰는 위젯 키 — 단일 인스턴스 위젯의 id 와 같다(kpi 는 `kpi-rev` 꼴).
 * multi 위젯(캘린더)은 인스턴스 id 가 `calendar-<ts>` 라 타입으로 묶는다.
 */
export function widgetKey(w: WidgetRef): string {
  return w.type === "kpi" && w.kpiIndex !== undefined ? kpiWidgetId(w.kpiIndex) : w.type;
}

/** 그룹 문서 home_widgets — 키별 on/off. 값이 없으면 켜짐 */
export type HomeWidgetToggles = Record<string, boolean>;

/** 그룹 설정에서 꺼진 위젯 키 집합 */
export function hiddenWidgetKeys(toggles: HomeWidgetToggles | null | undefined): ReadonlySet<string> {
  const out = new Set<string>();
  if (!toggles) return out;
  for (const [k, v] of Object.entries(toggles)) if (v === false) out.add(k);
  return out;
}

/** 이 사업 홈에 둘 수 있는 위젯 목록(경원 아스콘은 ascon 위젯만) */
export function widgetsForBiz(ascon: boolean): HomeWidgetDef[] {
  return HOME_WIDGETS.filter((d) => !ascon || d.ascon);
}

/**
 * 위젯이 쓰는 출처 권한을 모두 가졌는지 — 정의가 없는(옛) 위젯은 숨긴다.
 * ascon = 경원 아스콘 공간(경원 자료 있는 위젯만). hidden = 그룹 설정에서 끈 위젯 키(관리자는 빈 집합).
 */
export function canShowWidget(
  w: WidgetRef,
  hasPerm: (k: string) => boolean,
  ascon = false,
  hidden?: ReadonlySet<string>,
): boolean {
  const def = widgetDef(w);
  if (!def) return false;
  if (ascon && !def.ascon) return false;
  if (hidden && hidden.has(widgetKey(def))) return false;
  return def.sources.every((src) => SOURCE_PERMS[src].some((k) => hasPerm(k)));
}
